import { useEffect, useRef, useState } from 'react'

import { toast } from '@utils/functions/notificationToast'

export type EmptySearchResultSectionKey =
  | 'weeklyStatus'
  | 'hierarchy'
  | 'consolidatedSummary'
  | 'unitClassification'

type SectionStatus = 'pending' | 'empty' | 'has-data'

const SECTION_LABELS: Record<EmptySearchResultSectionKey, string> = {
  weeklyStatus: 'Status da Semana',
  hierarchy: 'Fluxo de Aprovação',
  consolidatedSummary: 'Resumo Financeiro Consolidado',
  unitClassification: 'Classificação das Unidades'
}

const INITIAL_STATUS: Record<EmptySearchResultSectionKey, SectionStatus> = {
  weeklyStatus: 'pending',
  hierarchy: 'pending',
  consolidatedSummary: 'pending',
  unitClassification: 'pending'
}

// Toast único quando nenhuma das guias encontrou resultado, ou um toast por guia vazia caso só algumas voltem sem dados
export const useEmptySearchResultsToast = (searchSignature: string) => {
  const [status, setStatus] = useState(INITIAL_STATUS)

  const lastSignatureRef = useRef<string | null>(null)
  const hasToastedRef = useRef(false)

  useEffect(() => {
    if (lastSignatureRef.current === searchSignature) return

    lastSignatureRef.current = searchSignature
    hasToastedRef.current = false
    setStatus(INITIAL_STATUS)
  }, [searchSignature])

  useEffect(() => {
    if (hasToastedRef.current) return

    const entries = Object.entries(status) as [EmptySearchResultSectionKey, SectionStatus][]
    const isSettled = entries.every(([, sectionStatus]) => sectionStatus !== 'pending')

    if (!isSettled) return

    hasToastedRef.current = true

    const emptyKeys = entries.filter(([, sectionStatus]) => sectionStatus === 'empty').map(([key]) => key)

    if (emptyKeys.length === entries.length) {
      toast.warning('Não foi possível encontrar nenhum resultado com os filtros selecionados.')
      return
    }

    emptyKeys.forEach((key) => {
      toast.warning(`Não foi possível encontrar resultados em "${SECTION_LABELS[key]}" com os filtros selecionados.`)
    })
  }, [status])

  const reportSearchResult = (key: EmptySearchResultSectionKey, hasData: boolean) => {
    setStatus((prev) => {
      const nextStatus: SectionStatus = hasData ? 'has-data' : 'empty'
      return prev[key] === nextStatus ? prev : { ...prev, [key]: nextStatus }
    })
  }

  return { reportSearchResult }
}
